import {
  Component,
  input,
  output,
  computed,
  inject,
  effect,
  untracked,
} from '@angular/core'
import { CellClassFunc, ColDef, ColTypeDef, GetRowIdFunc, GridApi, GridReadyEvent } from 'ag-grid-community'
import { AgGridAngular } from 'ag-grid-angular'
import { findIndex, isEmpty, isEqual } from 'lodash'
import { NumberCellEditorComponent } from './number-cell-editor.component'
import { DateCellEditorComponent, DateCellEditorParams } from './date-cell-editor.component'
import { TextCellEditorComponent } from './text-cell-editor.component'
import { noTerminationTerminationDate } from '../shared/utilities/date'
import { Observable } from 'rxjs'
import { EditableTableToolbarComponent } from './editable-table-toolbar.component'
import { StringRecord } from '../shared/models/general'
import { EditableRow, EditableRowField, EffectiveDateProperties, ValidationMessage } from './entity-metadata'
import { toObservable } from '@angular/core/rxjs-interop'
import { SelectCellEditorComponent, SelectCellEditorParams } from './select-cell-editor.component'
import { addDays } from 'date-fns'
import { ChangeSetDescriptor, EntityManagementChangesService } from './entity-management-changes.service'
import { booleanFormatter, booleanOptions } from '../shared/utilities/formatters'

type Changes<T> = readonly EditableRow<T>[]

export type EditorSpec<T> = Readonly<{
  columns: readonly ColDef[]
  getId: (row: EditableRow<T>) => string
  initNewRow: () => Partial<T>
  validate: (rows: readonly EditableRow<T>[]) => readonly ValidationMessage[]
  save: (rows: readonly EditableRow<T>[]) => Observable<unknown>
}>

const coerce = <T> (field: EditableRowField<T>, rawValue: unknown): unknown => {
  const coerceValue = () => {
    if (typeof rawValue === 'string') {
      return rawValue.trim() || undefined
    } else if (rawValue == null) {
      return undefined
    } else {
      return rawValue
    }
  }

  const value = coerceValue()
  if (value === undefined && field === 'termination_date') {
    return noTerminationTerminationDate
  } else {
    return value
  }
}

@Component({
  selector: 'app-editable-table',
  imports: [
    AgGridAngular,
    EditableTableToolbarComponent,
  ],
  template: `
    <app-editable-table-toolbar
        [saveDisabled]="saveDisabled()"
        [addNewDisabled]="addNewDisabled()"
        [undoDisabled]="undoDisabled()"
        [labels]="labels()"
        [validationMessages]="validationMessages()"
        (addNew)="onAddNew()"
        (save)="onSave()"
        (undo)="onUndo()"
        (messageClick)="onMessageClick($event)"
    />
    <ag-grid-angular
        class="app-table"
        [defaultColDef]="defaultColDef"
        [columnTypes]="columnTypes"
        [columnDefs]="colDefs()"
        [rowData]="rowData()"
        [getRowId]="getRowId"
        [suppressNoRowsOverlay]="true"
        [suppressPaginationPanel]="true"
        [suppressDragLeaveHidesColumns]="true"
        [pagination]="false"
        [enableCellTextSelection]="true"
        [singleClickEdit]="true"
        [suppressRowHoverHighlight]="true"
        (gridReady)="onGridReady($event)"
    />
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;

      app-editable-table-toolbar {
        margin-bottom: 1rem;
      }
      
      .app-table {
        width: 100%;
        flex-basis: 0;
        flex-grow: 1;
      }
    }
  `,
})
export class EditableTableComponent<T extends EffectiveDateProperties> {
  private readonly changesService = inject(EntityManagementChangesService)

  private gridApi: GridApi | undefined

  readonly changeSetDescriptor = input.required<ChangeSetDescriptor>()
  readonly spec = input.required<EditorSpec<T>>()
  readonly rows = input.required<readonly T[]>()
  readonly groupBy = input<EditableRowField<T>>()
  readonly maxNewRows = input(999)

  readonly saveComplete = output()
  readonly error = output<Error>()

  private readonly changes = computed((): Changes<T> =>
    this.changesService.getChanges<T>(this.changeSetDescriptor().url),
  )

  private readonly newRows = computed((): Changes<T> => this.changes().filter(_ => _.new))

  protected readonly validationMessages = computed((): readonly ValidationMessage[] =>
    this.changesService.getValidationMessages(this.changeSetDescriptor().url),
  )

  protected readonly rowData = computed((): EditableRow<T>[] => {
    const changes = this.changes()
    const rows = this.rows() as T[]
    if (changes.length > 0) {
      const { getId } = this.spec()
      const updatedRows = changes.filter(_ => !_.new)

      const getRow = (raw: T): EditableRow<T> => {
        const id = getId(raw)
        return updatedRows.find(row => getId(row) === id) ?? raw
      }

      return [...rows.map(getRow), ...this.newRows()]
    } else {
      return rows
    }
  })

  private onSelect = (row: EditableRow<T>, field: string, value: unknown) => {
    this.setValue(row, field as EditableRowField<T>, value)
  }

  private readonly dateCellEditorParams: DateCellEditorParams<T> = {
    onSelect: this.onSelect,
  }

  private readonly booleanCellEditorParams: SelectCellEditorParams<T> = {
    options: [booleanOptions.true, booleanOptions.false],
    onSelect: this.onSelect,
  }

  protected readonly columnTypes: Readonly<Record<string, ColTypeDef<EditableRow<T>>>> = {
    date: { cellEditor: DateCellEditorComponent, cellEditorParams: this.dateCellEditorParams },
    number: { cellEditor: NumberCellEditorComponent },
    boolean: {
      // workaround for an apparent ag grid bug
      // grid will default to a checkbox renderer until the item is changed oddly enough
      // cellRenderer fn params (apparently not documented) seem to be pretty much the same as ValueFormatterParams
      valueFormatter: booleanFormatter,
      cellRenderer: booleanFormatter,
      cellEditor: SelectCellEditorComponent,
      cellEditorParams: this.booleanCellEditorParams,
    },
  }

  protected readonly defaultColDef: ColDef<EditableRow<T>> = {
    sortable: false,
    filter: false,
    editable: false,
    width: 130,
  }

  // these classes can't be in this component's stylesheet easily
  // so the solution is to make them global to all app-table grids - see styles.scss
  private readonly getCellClass: CellClassFunc<EditableRow<T>> = ({ data, colDef }): string[] => {
    const field = colDef.field as EditableRowField<T>
    const id = data ? this.spec().getId(data) : ''
    const validationMessages = this.validationMessages()

    const getAlignmentClass = (): string | undefined =>
      colDef.type === 'number' && colDef.editable ? 'ag-right-aligned-cell' : undefined

    const isModified = () => {
      if (data?.new) {
        return true
      } else {
        const original = data?.original
        return original && !isEqual(data[field], original[field])
      }
    }

    const getModifiedClass = (): string | undefined => isModified() ? 'editable-cell-modified' : undefined

    const hasMessage = () =>
      validationMessages.some(message =>
        message.targets.some(target => target.id === id && target.field === field),
      )

    const getErrorClass = (): string | undefined => hasMessage() ? 'editable-cell-error' : undefined

    return [
      getAlignmentClass(),
      getModifiedClass(),
      getErrorClass(),
    ].filter((s): s is string => Boolean(s))
  }

  protected readonly colDefs = computed((): ColDef<EditableRow<T>>[] => {
    const getEditingExtensions = (rawColDef: ColDef): Partial<ColDef<EditableRow<T>>> => {
      if (rawColDef.editable) {
        const getSelectCellEditorParams = (): SelectCellEditorParams<T> => {
          const cellEditorParams = rawColDef.cellEditorParams as SelectCellEditorParams<T>
          return {
            ...cellEditorParams,
            onSelect: this.onSelect,
          }
        }

        return {
          cellClass: this.getCellClass,
          valueSetter: ({ data, newValue }): boolean =>
            this.setValue(data, rawColDef.field as EditableRowField<T>, newValue),
          ...rawColDef.type || rawColDef.cellEditor ? {} : { cellEditor: TextCellEditorComponent },
          ...rawColDef.cellEditor === SelectCellEditorComponent ? { cellEditorParams: getSelectCellEditorParams() } : {},
        }
      } else {
        return {
          cellClass: 'not-editable-cell', // see getCellClass comment
        }
      }
    }

    return this.spec().columns.map(raw => ({
      ...raw as ColDef<EditableRow<T>>,
      ...getEditingExtensions(raw),
    }))
  })

  protected readonly labels = computed((): StringRecord => {
    const labels: Record<string, string> = {}
    this.colDefs().forEach(colDef => {
      if (colDef.field && colDef.headerName) {
        labels[colDef.field] = colDef.headerName
      }
    })
    return labels
  })

  protected readonly saveDisabled = computed((): boolean =>
    this.changes().length === 0 || this.validationMessages().length > 0,
  )

  protected readonly addNewDisabled = computed((): boolean => this.newRows().length >= this.maxNewRows())

  protected readonly undoDisabled = computed((): boolean =>
    isEmpty(this.changes()) && this.newRows().length === 0,
  )

  protected readonly getRowId: GetRowIdFunc<EditableRow<T>> = ({ data }) =>
    this.spec().getId(data)

  constructor() {
    // workaround to make sure the grid is repainted (error indicator) when validation messages change
    // to reproduce the issue, make an effective date overlap with a termination date
    // the termination date row does not get repainted
    // tried ChangeDetectorRef, using a valueGetter, etc. and this is the only thing that works
    toObservable(this.validationMessages).subscribe(() => {
      window.setTimeout(() => {
        this.gridApi?.refreshCells({ force: true })
      }, 0)
    })

    effect(() => {
      const descriptor = this.changeSetDescriptor()

      untracked(() => {
        this.changesService.initChangeSet(descriptor)
      })
    })
  }

  protected onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api
  }

  private setValue({ original, ...row }: EditableRow<T>, field: EditableRowField<T>, rawValue: unknown): boolean {
    const spec = this.spec()
    const value = coerce(field, rawValue)
    const id = spec.getId(row as EditableRow<T>)
    const updated: Record<string, unknown> = { ...row }
    if (value === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete updated[field]
    } else {
      updated[field] = value
    }
    if (isEqual(row, updated)) {
      return false
    } else if (row.new) {
      this.updateChangeSet(changes => changes.map(row => spec.getId(row) === id ? updated as EditableRow<T> : row))
      if (field === 'effective_date' && value) {
        const groupBy = this.groupBy()
        // terminate old using effective date for new
        const oldRow = this.rowData().find(r => {
          if (r.new || (groupBy && r[groupBy] != updated[groupBy])) {
            return false
          } else {
            return r.termination_date?.startsWith('9')
          }
        })
        if (oldRow) {
          this.setValue(oldRow, 'termination_date', addDays(value as string, -1).toJSON())
        }
      }
    } else if (original && isEqual(updated, original)) {
      this.updateChangeSet(changes => changes.filter(row => spec.getId(row) !== id))
    } else if (original) {
      const updatedRow = { original, ...updated } as EditableRow<T>
      this.updateChangeSet(changes => changes.map(row => spec.getId(row) === id ? updatedRow : row))
    } else {
      const newChange = { original: row, ...updated } as EditableRow<T>
      this.updateChangeSet(changes => [...changes, newChange])
    }
    return true
  }

  protected onAddNew() {
    this.updateChangeSet(changes => [
      ...changes,
      {
        ...this.spec().initNewRow(),
        termination_date: noTerminationTerminationDate,
        new: true,
      },
    ])
  }

  private updateChangeSet(updateFn: (oldChanges: Changes<T>) => Changes<T>) {
    const { url } = this.changeSetDescriptor()
    this.changesService.updateChanges(url, updateFn)
    this.changesService.setValidationMessages(url, this.spec().validate(this.rowData()))
  }

  protected onUndo() {
    this.gridApi?.stopEditing(true)
    this.changesService.undoAll(this.changeSetDescriptor().url)
  }

  protected onSave() {
    this.gridApi?.stopEditing()
    window.setTimeout(() => {
      if (!this.saveDisabled()) {
        this.save()
      }
    }, 0)
  }

  private save() {
    this.spec().save(this.changes()).subscribe({
      next: () => {
        this.onUndo()
        this.saveComplete.emit()
      },
      error: e => {
        this.error.emit(e as Error)
      },
    })
  }

  protected onMessageClick(message: ValidationMessage) {
    const target= message.targets[0]
    if (this.gridApi && target?.field) {
      this.gridApi.stopEditing()
      const spec = this.spec()
      const rowIndex = findIndex(this.rowData(), r => spec.getId(r) === target.id)
      if (rowIndex >= 0) {
        this.gridApi.startEditingCell({ rowIndex, colKey: target.field })
      }
    }
  }
}
